import { useQuery } from '@tanstack/react-query';
import { useTemplateSecrets } from '@backstage/plugin-scaffolder-react';
import { QUERY_KEYS } from '../../queryKeys';

export type AzureSubscription = {
  subscriptionId: string;
  displayName: string;
};

const AZ_USER_TOKEN_SECRET = 'AZURE_AZ_USER_TOKEN';
const ARM_SUBSCRIPTIONS_URL = 'https://management.azure.com/subscriptions?api-version=2022-12-01';

type ArmSubscription = {
  subscriptionId: string;
  displayName: string;
  state: string;
};

export const useAzureSubscriptions = () => {
  // Relies on AzUserTokenExtension having populated `AZURE_AZ_USER_TOKEN` in
  // template secrets earlier in the same template run. The query stays
  // disabled until the secret is present.
  const { secrets } = useTemplateSecrets();
  const token = secrets[AZ_USER_TOKEN_SECRET];
  const hasToken = Boolean(token);

  const getSubscriptions = async (): Promise<AzureSubscription[]> => {
    const response = await fetch(ARM_SUBSCRIPTIONS_URL, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      throw new Error(`Failed to fetch Azure subscriptions: ${response.statusText}`);
    }
    const data: { value: ArmSubscription[] } = await response.json();
    return data.value
      .filter(sub => sub.state === 'Enabled')
      .map(({ subscriptionId, displayName }) => ({ subscriptionId, displayName }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  };

  const {
    data: subscriptions,
    isLoading,
    error,
  } = useQuery({
    queryKey: QUERY_KEYS.scaffolder.azureSubscriptions(),
    queryFn: getSubscriptions,
    enabled: hasToken,
    // Disable caching for this query
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
  });

  return { subscriptions, isLoading, error, hasToken };
};
